import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Toast from "../Toast";
import SearchBar from "./SearchBar";
import SearchModeToggle from "./SearchModeToggle";
import SearchResultPanel from "./SearchResultPanel";
import useGpsSearch from "../../hooks/useGpsSearch";
import useLocationSearch from "../../hooks/useLocationSearch";
import useSoilData from "../../hooks/useSoilData";

// -------------------------------------------------------------
// SearchPanel
// The main search UI for the map. Bundles together the mode
// toggle, search bar, result panel, and toast notifications
// into one self-contained component.
//
// Supports two search modes:
// - Location mode: user types a place name, Nominatim converts
//   it into coordinates
// - GPS mode: user enters raw coordinates, Nominatim converts
//   them into a human-readable place name
//
// Flow:
// 1. User picks a search mode and submits a search
// 2. Coordinates are validated against the Kaduna/Kano study area
// 3. A result panel appears with the location details
// 4. User clicks "Get Soil Info" to fetch soil data for that location
// 5. User can close the panel to reset everything and start over
// -------------------------------------------------------------

function SearchPanel() {
  const { t } = useTranslation();
  // States
  const [toastMessage, setToastMessage] = useState(null);
  const [searchMode, setSearchMode] = useState("location");
  const [hasRequestedSearchSoilData, setHasRequestedSearchSoilData] = useState(false); // Tracks if the user has clicked "Get Soil Info". Prevents soil results from showing up before the user actually asks for them.

  // Custom hook to search for a location by GPS coordinates, validate it's within the study area, and get its name.
  const {
    searchByGps,
    coordinates: gpsCoordinates,
    locationName: gpsLocationName,
    isLoading: gpsSearchLoading,
    error: gpsError,
    reset: resetSearchedGps,
  } = useGpsSearch();

  // Custom hook to search for a location by name, validate it's within the study area, and get its coordinates.
  const {
    searchLocation,
    coordinates: searchedCoordinates,
    locationName: searchedLocationName,
    isLoading: isSearchLoading,
    error: locationError,
    reset: resetSearchedLocation,
  } = useLocationSearch();

  // Combine errors from both search methods into a single variable for easier handling.
  const searchError = locationError || gpsError;

  // Toast message effect: shows any search errors in a toast and automatically clears after 8 seconds.
  useEffect(() => {
    if (!searchError) return undefined;

    setToastMessage(searchError);
    const timeoutId = setTimeout(() => setToastMessage(null), 8000);

    return () => clearTimeout(timeoutId);
  }, [searchError]);

  // Custom hook using TanStack Query to fetch soil data from Supabase
  const {
    fetchSoilData,
    soilData: searchSoilData,
    recommendation: searchRecommendation,
    isLoadingSoilData: isSoilLoading,
    soilDataError: soilError,
    reset: resetSoilData,
  } = useSoilData();

  // Use GPS coordinates if available, otherwise use searched location coordinates.
  const activeCoordinates = gpsCoordinates ?? searchedCoordinates;

  // Use GPS location name if available, otherwise use searched location name.
  const activeLocationName = gpsLocationName ?? searchedLocationName;

  // Handles searches from the search bar, either by location name or GPS coordinates depending on the active search mode.
  const handleSearch = async (value) => {
    if (!value) {
      setToastMessage(`⚠️ ${t("search.pleaseEnterLocationOrGps")}`);
      return;
    }

    setHasRequestedSearchSoilData(false);
    resetSoilData();
    setToastMessage(null);

    if (searchMode === "gps") {
      resetSearchedLocation();
      await searchByGps({ lat: value?.lat, lng: value?.lng });
      return;
    }

    resetSearchedGps();
    await searchLocation(value);
  };

  // Fetches soil data when the user clicks "Get Soil Info".
  const handleSearchSoilInfo = async () => {
    if (!activeCoordinates) return;

    setHasRequestedSearchSoilData(true);
    const { lat, lng } = activeCoordinates;

    try {
      await fetchSoilData({ lat, lng });
    } catch (err) {
      // React Query stores the error for the panel UI; swallow here to avoid uncaught runtime overlays.
    }
  };

  // Resets all search and soil data state when the user closes the search result panel.
  const handleSearchReset = () => {
    setHasRequestedSearchSoilData(false);
    resetSearchedGps();
    resetSearchedLocation();
    resetSoilData();
  };

  return (
    <>
      {/* Search mode toggle button */}
      <SearchModeToggle searchMode={searchMode} onToggle={setSearchMode} />

      {/* Search bar */}
      <SearchBar
        searchMode={searchMode}
        onSearch={handleSearch}
        isLoading={searchMode === "gps" ? gpsSearchLoading : isSearchLoading}
      />

      {/* Toast message */}
      {toastMessage && <Toast message={toastMessage} />}

      {/* Search result panel */}
      {(activeCoordinates || isSoilLoading) && (
        <SearchResultPanel
          locationName={activeLocationName}
          coordinates={activeCoordinates}
          soilData={searchSoilData}
          recommendation={searchRecommendation}
          loading={isSoilLoading}
          error={soilError}
          hasRequestedSoilData={hasRequestedSearchSoilData}
          onGetSoilInfo={handleSearchSoilInfo}
          onClose={handleSearchReset}
          confidenceScore={searchSoilData?.confidence_score}
          confidenceInterpretation={searchSoilData?.interpretation}
          pointsUsed={searchSoilData?.points_used}
          isInterpolated={searchSoilData?.is_interpolated}
        />
      )}
    </>
  );
}

export default SearchPanel;
